import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { buildQueryString } from '@/lib/api/client';
import { recipientsFetch } from '@/lib/recipients/api';
import { suppressionKeys, recipientKeys } from '@/hooks/api/query-keys';
import type { PaginatedResult } from '@/hooks/api/pagination';
import { usePermissionGate } from '@/components/auth/permission-gate';

// ============ Types ============

export interface SuppressionEntry {
  id: string;
  email: string;
  reason: string;
  source: string;
  createdAt: string;
  createdByName: string;
  createdById: string;
}

// ============ Query Hooks ============

export function useSuppressionList(filter?: Record<string, unknown> & { page?: number; limit?: number }) {
  const defaultLimit = filter?.limit ?? 25;
  return useQuery({
    queryKey: suppressionKeys.list(filter),
    queryFn: async () => {
      const queryFilter = filter
        ? {
            ...filter,
            pageSize: filter.limit ?? defaultLimit,
          }
        : undefined;
      if (queryFilter) {
        delete (queryFilter as { limit?: number }).limit;
      }
      const qs = queryFilter ? buildQueryString(queryFilter) : '';
      return recipientsFetch<
        { data: SuppressionEntry[]; total: number; page?: number; limit?: number; totalPages?: number } | SuppressionEntry[]
      >(`suppression${qs}`);
    },
    select: (raw): PaginatedResult<SuppressionEntry> => {
      if (Array.isArray(raw)) {
        return { data: raw, total: raw.length, page: 1, limit: raw.length || defaultLimit, totalPages: 1 };
      }
      const limit = raw.limit ?? defaultLimit;
      const total = raw.total ?? 0;
      return {
        data: raw.data ?? [],
        total,
        page: raw.page ?? 1,
        limit,
        totalPages: raw.totalPages ?? Math.max(1, Math.ceil(total / limit)),
      };
    },
  });
}

export function useSuppressionMetrics() {
  return useQuery({
    queryKey: suppressionKeys.metrics(),
    queryFn: () =>
      recipientsFetch<{
        totalSuppressed: number;
        byReason: Record<string, number>;
        recentAdditions: number;
      }>('suppression/metrics'),
  });
}

// ============ Mutation Hooks ============
//
// Failure messages belong to the CALLER (audit 2026-09-25, "double toasts").
// Every screen that calls these hooks reports a failure itself — a toast with
// the server's reason, or the import dialog's inline error — and each hook
// used to toast `err.message` as well, so one failure said so twice. These
// hooks do not toast; lib/__tests__/suppression-mutation-errors-handled.guard
// .spec.ts fails if a caller of one of them leaves a failure unreported.

// Exported so every UI surface that offers a Reason picker (the Edit dialog,
// #297's Add sidepanel, #294's Advanced Search field list) draws from the
// SAME 6-value enum the backend's `recipient_suppressions.reason` column
// accepts, instead of drifting.
export const SUPPRESSION_REASONS = ['Bounced', 'Unsubscribed', 'Complaint', 'Manual', 'Legal', 'Other'] as const;
const VALID_REASONS = SUPPRESSION_REASONS;
type SuppressionReason = typeof VALID_REASONS[number];

function normalizeReason(reason: string): SuppressionReason {
  const normalized = String(reason ?? '').trim().toLowerCase();
  const found = VALID_REASONS.find(r => r.toLowerCase() === normalized);
  return found ?? 'Manual';
}

export function useAddSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    // F10 (audit 2026-09-25) — `description` is stored by the API
    // (SuppressEmailDto); Manual Entry's note used to be dropped here.
    mutationFn: (data: { email: string; reason: string; description?: string }) =>
      recipientsFetch<SuppressionEntry>('suppression', {
        method: 'POST',
        body: JSON.stringify({ ...data, reason: normalizeReason(data.reason) }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suppressionKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
  });
}

export function useRemoveSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<void>(`suppression/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suppressionKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
  });
}

export function useUpdateSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      recipientsFetch<SuppressionEntry>(`suppression/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ reason: normalizeReason(reason) }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suppressionKeys.all() });
    },
  });
}

/**
 * The suppression list's server filters, as the list query and a select-all
 * Remove both send them (F11, audit 2026-09-25).
 */
export interface SuppressionListFilterParams {
  search?: string;
  from?: string;
  to?: string;
  advancedFilters?: { field: string; operator: string; value: string }[];
  advancedMatch?: 'AND' | 'OR';
}

/** POST /recipients/suppression/bulk-delete — ids, or every entry the filters match. */
export type SuppressionBulkDeleteRequest =
  | { ids: string[] }
  | { selectAll: true; filters: SuppressionListFilterParams };

export function useBulkDeleteSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SuppressionBulkDeleteRequest) =>
      recipientsFetch<{ deleted: number }>('suppression/bulk-delete', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suppressionKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
  });
}

export function useBulkSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    // API expects { entries: [{ email, reason }] } — transform from the { emails, reason } shape used by callers
    mutationFn: (data: { emails: string[]; reason: string }) => {
      const normalizedReason = normalizeReason(data.reason);
      return recipientsFetch<{ added: number; skipped: number }>('suppression/bulk', {
        method: 'POST',
        body: JSON.stringify({
          entries: data.emails.map(email => ({ email, reason: normalizedReason })),
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suppressionKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
  });
}

// ============ #295 — CSV import (row-tolerant) ============

export interface ImportCsvSuppressionRow {
  email: string;
  reason?: string;
  description?: string;
}

export interface ImportCsvSuppressionResult {
  added: number;
  skipped: number;
  errors: number;
}

/**
 * #295 — `POST /recipients/suppression/bulk` (useBulkSuppression above) has
 * `@IsEmail()` on every array entry, so the global ValidationPipe's
 * whitelist + forbidNonWhitelisted rejects the WHOLE array on one bad row —
 * an all-or-nothing import. `import-csv` is a purpose-built, ALREADY-TESTED
 * endpoint (`RecipientSuppressionService.importCsv`) that validates row by
 * row and imports the rest, returning `{ added, skipped, errors }` so the
 * caller can report exactly what happened instead of guessing. Reason
 * normalization/defaulting happens server-side — send the raw string.
 */
export function useImportCsvSuppression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rows: ImportCsvSuppressionRow[]) =>
      recipientsFetch<ImportCsvSuppressionResult>('suppression/import-csv', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suppressionKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
  });
}

// ============ #301 — bulk suppression-status cross-check ============

export interface SuppressionCheckEntry {
  email: string;
  suppressed: boolean;
  reason: string | null;
}

/**
 * #301 — batch-checks which of `emails` are ALREADY actively suppressed, so
 * a recipient picker (Add Suppression sidepanel, the /add page's
 * SelectRecipientsTable) can mark/exclude them instead of silently offering
 * a re-suppress that the backend will report back as `skipped`. Disabled
 * when `emails` is empty so an unmounted/loading picker doesn't fire a
 * request with nothing to check.
 *
 * The role (browser QA, 2026-10-06): the check is manage:Recipient
 * (suppression.controller.ts), and the list page mounts the Add sidepanel
 * closed, so every read-only visit sent it twice and met two 403s. It is
 * asked only once the grants are KNOWN to include manage:Recipient — never
 * while they load (a fail-open "not denied" still fires in that window).
 */
export function useCheckSuppressionBulk(emails: string[]) {
  const normalized = [...new Set(emails.map((e) => e.toLowerCase().trim()))].sort();
  const gate = usePermissionGate('manage', 'Recipient');
  return useQuery({
    queryKey: [...suppressionKeys.all(), 'check-bulk', normalized],
    queryFn: () =>
      recipientsFetch<{ results: SuppressionCheckEntry[] }>('suppression/check', {
        method: 'POST',
        body: JSON.stringify({ emails: normalized }),
      }),
    enabled: normalized.length > 0 && !gate.loading && !gate.denied,
    staleTime: 30_000,
  });
}
